import type { ThemePerformanceSortHorizon } from '@api/api/theme-overview';
import semanticColorStyles from '@design/styles/SemanticColor.module.scss';
import { formatNumber } from '@terminal/views/dashboard/calendar-widget/calendar-event-drawer/drawer-content/utils/number-formatter';
import clsx from 'clsx';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './ChartTitle.module.scss';

export type PickedThemePerformanceSortHorizon = Extract<
  ThemePerformanceSortHorizon,
  'intraday' | '1w' | '1m' | '3m' | '6m' | '1y'
>;

export const isValidHorizon = (
  horizon: string | null | undefined
): horizon is PickedThemePerformanceSortHorizon => {
  return (
    typeof horizon === 'string' &&
    ['intraday', '1w', '1m', '3m', '6m', '1y'].includes(horizon)
  );
};

interface ChartTitleProps {
  description: ReactNode;
  activeHorizon: PickedThemePerformanceSortHorizon;
  horizonReturnValue?: number;
}

export const ChartTitle = ({
  description,
  activeHorizon,
  horizonReturnValue,
}: ChartTitleProps) => {
  const { t } = useTranslation(['widget']);

  const horizons: Record<PickedThemePerformanceSortHorizon, string> = {
    intraday: t('widget:dashboardThemePerf.chartTitle.horizon_1d'),
    '1w': t('widget:dashboardThemePerf.chartTitle.horizon_1w'),
    '1m': t('widget:dashboardThemePerf.chartTitle.horizon_1m'),
    '3m': t('widget:dashboardThemePerf.chartTitle.horizon_3m'),
    '6m': t('widget:dashboardThemePerf.chartTitle.horizon_6m'),
    '1y': t('widget:dashboardThemePerf.chartTitle.horizon_1y'),
  };

  const horizonReturn = formatNumber({
    isPercentage: true,
    value: horizonReturnValue,
  });

  return (
    <div className={styles.root}>
      <p className={styles.description}>{description}</p>
      <div
        className={clsx(styles.pill, {
          [styles.negative]: !!horizonReturnValue && horizonReturnValue < 0,
        })}
        data-testid="theme-performance-pill"
      >
        <span className={styles.horizonLabel}>{horizons[activeHorizon]}</span>
        <span
          className={clsx(
            styles.returnValue,
            !!horizonReturnValue && horizonReturnValue < 0
              ? semanticColorStyles.directionNegative
              : semanticColorStyles.directionPositive
          )}
        >
          {horizonReturn}
        </span>
      </div>
    </div>
  );
};
